import { EXAMPLE_FOLLOWUP_PROMPTS } from "@/content/ai/utils/AIGenerationUtils"
import { useLabel } from "@/core/context/LabelsContext"
import FormStore from "@/core/form/store/FormStore"
import MediaLibraryButton from "@/media/add/MediaLibraryButton"
import { GlobalID } from "@/relay/RelayTypes"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import AIButton from "@components/ai/AIButton"
import AIInput from "@components/ai/AIInput"
import AIText from "@components/ai/AIText"
import { AI_GENERATION_REFERENCE_TYPES } from "@components/dropzone/FileDropzone"
import Form from "@components/form/Form"
import { MediaResult } from "@components/media/upload/hooks/useMultipartUploadMediaToS3"
import {
  DiscoButton,
  DiscoCheckbox,
  DiscoDivider,
  DiscoDropdown,
  DiscoIcon,
  DiscoIconButton,
  DiscoSection,
  DiscoTooltip,
} from "@disco-ui"
import DiscoDropdownItem from "@disco-ui/dropdown/DiscoDropdownItem"
import { useTheme } from "@material-ui/core"
import { useIsMobile } from "@utils/hook/screenSizeHooks"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import useHasEntitlement from "@utils/hook/useHasEntitlement"
import { TestIDProps } from "@utils/typeUtils"
import { isValidUrl } from "@utils/url/urlUtils"
import { observer } from "mobx-react-lite"
import pluralize from "pluralize"
import { useEffect, useMemo, useRef, useState } from "react"
import AIFormAttachmentTags from "./AIFormAttachmentTags"
import AIReferencesListEmbeddingSourceSelect from "./AIReferencesListEmbeddingSourceSelect"
import GenerateWithAIFormToolDropdown from "./GenerateWithAIFormToolDropdown"

export function useGenerateWithAIForm() {
  const { aiSources } = useFeatureFlags()

  const initialFormState: GenerateWithAIFormState = {
    prompt: "",
    referenceUrls: [],
    assetEmbeddingSourceIds: [],
    contentEmbeddingSourceIds: [],
    trainingDataEmbeddingSourceIds: [],
    uploadedAssetIds: [],
    uploadedAssets: [],
    searchWeb: true,
    communityContext: Boolean(aiSources),
  }

  const [form] = useState(() => {
    return new FormStore<GenerateWithAIFormState, any>(
      initialFormState,
      noOpCommitter as any,
      {}
    )
  })
  return { form }

  function noOpCommitter() {
    return {
      dispose: () => {
        /* noop */
      },
    }
  }
}

export type GenerateWithAIFormState = {
  assetEmbeddingSourceIds?: GlobalID[] | null
  contentEmbeddingSourceIds?: GlobalID[] | null
  trainingDataEmbeddingSourceIds?: GlobalID[] | null
  uploadedAssets?: MediaResult[] | null
  uploadedAssetIds?: GlobalID[] | null
  referenceUrls?: string[] | null
  searchWeb?: boolean | null
  communityContext?: boolean | null
  prompt?: string | null
  referenceModuleContentUsageId?: GlobalID | null
}

export type GenerateWithAIFormStore = FormStore<GenerateWithAIFormState>

type GenerateWithAIFormProps = TestIDProps & {
  form: GenerateWithAIFormStore
  onSubmit: () => void
  moduleContentUsageId?: GlobalID | null
  minimized?: boolean
  disableSubmit?: boolean
  canUploadMedia?: boolean
  examplePrompts?: string[]
  placeholder?: string
  isLoading?: boolean
}

function GenerateWithAIForm(props: GenerateWithAIFormProps) {
  const { aiSources } = useFeatureFlags()
  const {
    form,
    onSubmit,
    moduleContentUsageId,
    testid = "GenerateContentWithAIForm",
    minimized = false,
    disableSubmit = false,
    canUploadMedia = false,
    examplePrompts,
    placeholder,
    isLoading = false,
  } = props

  const hasEntitlement = useHasEntitlement("ai_content_generation")
  const selectedItemsRef = useRef<HTMLDivElement>(null)
  const [tagsHeight, setTagsHeight] = useState(0)
  const isMobile = useIsMobile()
  const organizationLabel = useLabel("organization")

  // Update height when items change or when the div resizes
  useEffect(() => {
    if (!selectedItemsRef.current) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTagsHeight(entry.contentRect.height)
      }
    })

    resizeObserver.observe(selectedItemsRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, []) // Empty dependency array since we're using ResizeObserver

  const theme = useTheme()
  const [showAssetSelector, setShowAssetSelector] = useState(false)
  const [showContentSelector, setShowContentSelector] = useState(false)
  const [showLinkSelector, setShowLinkSelector] = useState(false)
  const [referenceUrl, setReferenceUrl] = useState("")

  const randomPlaceholder = useMemo(() => {
    if (minimized) {
      const randomIndex = Math.floor(Math.random() * EXAMPLE_FOLLOWUP_PROMPTS.length)
      return `Add a follow up prompt e.g. "${EXAMPLE_FOLLOWUP_PROMPTS[randomIndex]}"`
    }

    if (placeholder?.length) {
      return placeholder
    }

    if (examplePrompts?.length) {
      // Use prompts based on generationType
      const prompts = examplePrompts
      const randomIndex = Math.floor(Math.random() * prompts.length)
      return prompts[randomIndex]
    }

    return ""
  }, [minimized, examplePrompts, placeholder])

  const totalCount =
    (form.state.assetEmbeddingSourceIds?.length || 0) +
    (form.state.contentEmbeddingSourceIds?.length || 0) +
    (form.state.trainingDataEmbeddingSourceIds?.length || 0) +
    (form.state.referenceUrls?.length || 0) +
    (form.state.uploadedAssets?.length || 0)

  const mobileCount = totalCount + (form.state.referenceUrls?.length || 0)

  const classes = useStyles({
    totalCount,
    tagsHeight,
    selectorIsOpen: showLinkSelector || showContentSelector || showAssetSelector,
  })

  return (
    <DiscoSection className={classes.container}>
      <Form testid={`${testid}.form`} errorInfo={null}>
        <div className={classes.inputWrapper}>
          <AIInput
            testid={`${testid}.prompt-input`}
            value={form.state.prompt}
            onChange={handlePromptChange}
            disabled={!hasEntitlement}
            placeholder={randomPlaceholder}
            showBadge={false}
            minHeight={`${92 + tagsHeight}px`}
            maxRows={12}
            className={classes.input}
            onEnterPress={minimized ? handleSubmit : undefined}
          />
          <div ref={selectedItemsRef} className={classes.selectedItemsWrapper}>
            <AIFormAttachmentTags
              testid={testid}
              assetEmbeddingSourceIds={form.state.assetEmbeddingSourceIds || []}
              contentEmbeddingSourceIds={form.state.contentEmbeddingSourceIds || []}
              trainingDataEmbeddingSourceIds={
                form.state.trainingDataEmbeddingSourceIds || []
              }
              uploadedAssets={form.state.uploadedAssets || []}
              referenceUrls={form.state.referenceUrls || []}
              onRemoveAssetEmbeddingSource={handleRemoveAssetEmbeddingSource}
              onRemoveContentEmbeddingSource={handleRemoveContentEmbeddingSource}
              onRemoveTrainingDataEmbeddingSource={
                handleRemoveTrainingDataEmbeddingSource
              }
              onRemoveUploadedAsset={handleRemoveUploadedAsset}
              onRemoveReferenceUrl={handleRemoveReferenceUrl}
            />
          </div>

          {showAssetSelector && (
            <div className={classes.selectorWrapper}>
              <AIReferencesListEmbeddingSourceSelect
                entity={"asset"}
                values={form.state.assetEmbeddingSourceIds || []}
                onSelect={handleAssetEmbeddingSourceIdsSelect}
                testid={`${testid}.media-select`}
                isOpen={showAssetSelector}
                onClose={() => {
                  // Prevent dropdown from closing when pressing left/right arrow keys in the input
                  if (!document.activeElement?.matches('input[type="text"]')) {
                    setShowAssetSelector(false)
                  }
                }}
                onOpen={() => setShowAssetSelector(true)}
              />
            </div>
          )}

          {showContentSelector && (
            <div className={classes.selectorWrapper}>
              <AIReferencesListEmbeddingSourceSelect
                entity={"content"}
                values={form.state.contentEmbeddingSourceIds || []}
                onSelect={handleContentEmbeddingSourceIdsSelect}
                testid={`${testid}.content-select`}
                isOpen={showContentSelector}
                onClose={() => {
                  // Prevent dropdown from closing when pressing left/right arrow keys in the input
                  if (!document.activeElement?.matches('input[type="text"]')) {
                    setShowContentSelector(false)
                  }
                }}
                onOpen={() => setShowContentSelector(true)}
              />
            </div>
          )}

          {showLinkSelector && (
            <div className={classes.selectorWrapper}>
              <div className={classes.trainingDataSelectorContainer}>
                <AIReferencesListEmbeddingSourceSelect
                  entity={"training_data"}
                  values={form.state.trainingDataEmbeddingSourceIds || []}
                  onSelect={handleTrainingDataEmbeddingSourceIdsSelect}
                  testid={`${testid}.training-data-select`}
                  isOpen={showLinkSelector}
                  onClose={() => {
                    // Prevent dropdown from closing when pressing left/right arrow keys in the input
                    if (!document.activeElement?.matches('input[type="text"]')) {
                      setShowLinkSelector(false)
                    }
                  }}
                  onOpen={() => setShowLinkSelector(true)}
                  onInputChange={setReferenceUrl}
                  placeholder={"Select sources or add new link"}
                  onKeyDown={(e) => {
                    const isDisabled = isAddMultipleReferencesButtonDisabled()
                    if (isDisabled) return

                    if (e.key === "Enter") {
                      handleAddReferenceUrl()
                    }
                  }}
                />

                <DiscoTooltip
                  content={getDisabledAddReferencesButtonDisabledText()}
                  disabled={!isAddMultipleReferencesButtonDisabled()}
                >
                  <span>
                    <DiscoButton
                      onMouseDown={(e) => {
                        e.preventDefault() // Prevent the select from losing focus
                        handleAddReferenceUrl()
                      }}
                      disabled={isAddMultipleReferencesButtonDisabled()}
                    >
                      {"Add"}
                    </DiscoButton>
                  </span>
                </DiscoTooltip>
              </div>
            </div>
          )}

          <div className={classes.controls}>
            <div className={classes.leftControls}>
              <DiscoDropdown
                disableScrollLock
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                menuClasses={{ paper: classes.dropdown }}
                menuButton={({ onClick }) => (
                  <>
                    <DiscoIconButton
                      tooltip={
                        "Add attachments to enhance AI generation with relevant context"
                      }
                      onClick={onClick}
                      height={32}
                      width={32}
                      testid={`${testid}.attach-button`}
                      className={classes.controlButton}
                      color={mobileCount > 0 ? theme.palette.aiColors.blue : undefined}
                      svgStyles={{
                        width: 20,
                        height: 20,
                      }}
                    >
                      <DiscoIcon
                        icon={"add-circle"}
                        className={
                          mobileCount > 0 ? theme.palette.aiColors.blue : undefined
                        }
                      />
                    </DiscoIconButton>

                    {mobileCount > 0 && (
                      <AIText variant={"body-sm-700"}>{getDisplayText()}</AIText>
                    )}
                  </>
                )}
              >
                <DiscoDropdownItem
                  icon={<DiscoIcon icon={"video-square"} />}
                  title={"Attach Media"}
                  testid={`${testid}.attach-media`}
                  onClick={toggleAssetSelector}
                />

                <DiscoDropdownItem
                  icon={<DiscoIcon icon={"file"} />}
                  title={"Attach Content"}
                  testid={`${testid}.attach-content`}
                  onClick={toggleContentSelector}
                />

                {canUploadMedia && (
                  <MediaLibraryButton
                    allowedMimeTypes={["video/mp4", "application/pdf"]}
                    allowedFileTypes={["video", "document"]}
                    onUpload={handleAssetUpload}
                    onMediaSelect={handleAssetUpload}
                    dropzoneOptions={{ accept: AI_GENERATION_REFERENCE_TYPES }}
                    disabled={(form.state.uploadedAssets?.length || 0) >= 5}
                    PopoverProps={{
                      anchorOrigin: {
                        vertical: "center",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "bottom",
                        horizontal: "right",
                      },
                    }}
                  >
                    {(btnprops) => (
                      <DiscoDropdownItem
                        {...btnprops}
                        icon={<DiscoIcon icon={"upload"} />}
                        title={`Upload File${
                          (form.state.uploadedAssets?.length || 0) > 0
                            ? ` (${(form.state.uploadedAssets?.length || 0) / 5}`
                            : ""
                        }`}
                        testid={`${testid}.upload-file`}
                        disabled={(form.state.uploadedAssets?.length || 0) >= 5}
                      />
                    )}
                  </MediaLibraryButton>
                )}

                <DiscoDropdownItem
                  icon={<DiscoIcon icon={"text-link"} />}
                  title={"Add Link"}
                  testid={`${testid}.add-link`}
                  onClick={toggleLinkSelector}
                />
              </DiscoDropdown>

              <GenerateWithAIFormToolDropdown form={form} mobileCount={mobileCount} />

              {(form.state.searchWeb || form.state.communityContext) && (
                <>
                  <DiscoDivider
                    vertical
                    className={classes.divider}
                    color={theme.palette.groovy.neutral[300]}
                  />
                  {form.state.searchWeb && (
                    <DiscoIconButton
                      onClick={() => {
                        form.state.searchWeb = !form.state.searchWeb
                      }}
                      svgStyles={{
                        width: 16,
                        height: 16,
                      }}
                      testid={`${testid}.web-search-button`}
                      className={`${classes.controlButton} ${classes.aiToolButton}`}
                      hoverColor={theme.palette.aiColors.blue}
                      color={
                        form.state.searchWeb ? theme.palette.aiColors.blue : undefined
                      }
                      tooltip={
                        isMobile
                          ? "Enable web search to include relevant online resources in the generation."
                          : undefined
                      }
                    >
                      <DiscoIcon icon={"globe"} />
                      {form.state.searchWeb && !isMobile && !minimized && (
                        <AIText
                          variant={"body-sm-600"}
                          className={classes.aiToolLabelText}
                        >
                          {"Web Search"}
                        </AIText>
                      )}
                    </DiscoIconButton>
                  )}

                  {aiSources && form.state.communityContext && (
                    <DiscoIconButton
                      onClick={() => {
                        form.state.communityContext = !form.state.communityContext
                      }}
                      testid={`${testid}.community-context-button`}
                      className={`${classes.controlButton} ${classes.aiToolButton}`}
                      hoverColor={theme.palette.aiColors.blue}
                      color={
                        form.state.communityContext
                          ? theme.palette.aiColors.blue
                          : undefined
                      }
                      svgStyles={{
                        width: 16,
                        height: 16,
                      }}
                      tooltip={
                        isMobile
                          ? `Use all content from ${organizationLabel.singularLowercase} for the generation.`
                          : undefined
                      }
                    >
                      <DiscoIcon icon={"grid-thick"} />
                      {form.state.communityContext && !isMobile && !minimized && (
                        <AIText
                          variant={"body-sm-600"}
                          className={classes.aiToolLabelText}
                        >
                          {organizationLabel.singular}
                        </AIText>
                      )}
                    </DiscoIconButton>
                  )}
                </>
              )}
            </div>

            <AIButton
              disabled={disableSubmit || isLoading || !form.state.prompt?.trim()}
              onClick={onSubmit}
              className={classes.aiButton}
              shouldDisplaySpinner={isLoading}
              testid={`${testid}.generate-button`}
              noIcon
            >
              <DiscoIcon
                color={"white"}
                active
                icon={"iconsax.arrow-up"}
                height={20}
                width={20}
              />
            </AIButton>
          </div>
        </div>
        {moduleContentUsageId && (
          <DiscoCheckbox
            label={"Reference module content"}
            checked={!!form.state.referenceModuleContentUsageId}
            onChange={handleToggleShouldReferenceContentModule}
            testid={`${testid}.reference-module-checkbox`}
            classes={{
              root: classes.moduleContentCheckboxContainer,
            }}
          />
        )}
      </Form>
    </DiscoSection>
  )

  function getDisabledAddReferencesButtonDisabledText() {
    if ((form.state.referenceUrls?.length || 0) >= 5) {
      return "Can't add more than 5 new links"
    }

    return "Please enter a full URL, ie. https://www.disco.co rather than disco.co"
  }

  function isAddMultipleReferencesButtonDisabled() {
    return (
      (form.state.referenceUrls?.length || 0) >= 5 || // Can't add more than 5 new links
      !isValidUrl(referenceUrl.trim())
    )
  }

  function getDisplayText() {
    if (isMobile) {
      return mobileCount || ""
    }

    const parts = []
    const attachmentCount =
      totalCount -
      (form.state.trainingDataEmbeddingSourceIds?.length || 0) -
      (form.state.referenceUrls?.length || 0)

    const linkCount =
      (form.state.trainingDataEmbeddingSourceIds?.length || 0) +
      (form.state.referenceUrls?.length || 0)
    if (attachmentCount > 0) {
      parts.push(`${attachmentCount} ${pluralize("Attachment", attachmentCount)}`)
    }
    if (linkCount > 0) {
      parts.push(`${linkCount} ${pluralize("Link", linkCount)}`)
    }
    return parts.join(" + ")
  }

  function handleAssetUpload(result: MediaResult) {
    if ((form.state.uploadedAssets?.length || 0) >= 5) return
    form.state.uploadedAssetIds!.push(result.id)
    form.state.uploadedAssets!.push(result)
  }

  function handlePromptChange(e: React.ChangeEvent<HTMLInputElement>) {
    form.state.prompt = e.target.value || ""
  }

  function handleSubmit() {
    if (form.state.prompt?.trim()) {
      onSubmit()
    }
  }

  function handleAddReferenceUrl() {
    form.state.referenceUrls!.push(referenceUrl)
    setReferenceUrl("")
    setShowLinkSelector(false)
  }

  function handleAssetEmbeddingSourceIdsSelect(ids: GlobalID[]) {
    form.state.assetEmbeddingSourceIds!.replace(ids)
    setShowAssetSelector(false)
  }

  function handleContentEmbeddingSourceIdsSelect(ids: GlobalID[]) {
    form.state.contentEmbeddingSourceIds!.replace(ids)
    setShowContentSelector(false)
  }

  function handleTrainingDataEmbeddingSourceIdsSelect(ids: GlobalID[]) {
    form.state.trainingDataEmbeddingSourceIds!.replace(ids)
    setShowLinkSelector(false)
  }

  function toggleAssetSelector() {
    setShowAssetSelector(!showAssetSelector)
    setShowContentSelector(false)
    setShowLinkSelector(false)
  }

  function toggleContentSelector() {
    setShowAssetSelector(false)
    setShowContentSelector(!showContentSelector)
    setShowLinkSelector(false)
  }

  function toggleLinkSelector() {
    setShowAssetSelector(false)
    setShowContentSelector(false)
    setShowLinkSelector(!showLinkSelector)
  }

  function handleRemoveAssetEmbeddingSource(idToRemove: GlobalID) {
    form.state.assetEmbeddingSourceIds!.replace(
      form.state.assetEmbeddingSourceIds!.filter((id) => id !== idToRemove)
    )
  }

  function handleRemoveContentEmbeddingSource(idToRemove: GlobalID) {
    form.state.contentEmbeddingSourceIds!.replace(
      form.state.contentEmbeddingSourceIds!.filter((id) => id !== idToRemove)
    )
  }

  function handleRemoveTrainingDataEmbeddingSource(idToRemove: GlobalID) {
    form.state.trainingDataEmbeddingSourceIds!.replace(
      form.state.trainingDataEmbeddingSourceIds!.filter((id) => id !== idToRemove)
    )
  }

  function handleRemoveUploadedAsset(mediaToRemove: MediaResult) {
    form.state.uploadedAssetIds!.replace(
      form.state.uploadedAssetIds!.filter((id) => id !== mediaToRemove.id)
    )
    form.state.uploadedAssets!.replace(
      form.state.uploadedAssets!.filter((asset) => asset.id !== mediaToRemove.id)
    )
  }

  function handleRemoveReferenceUrl(url: string) {
    form.state.referenceUrls!.replace(form.state.referenceUrls!.filter((u) => u !== url))
  }

  function handleToggleShouldReferenceContentModule(isChecked: boolean) {
    if (isChecked) {
      form.state.referenceModuleContentUsageId = moduleContentUsageId
    } else {
      form.state.referenceModuleContentUsageId = null
    }
  }
}

interface StyleProps {
  tagsHeight: number
  totalCount: number
  selectorIsOpen: boolean
}

const useStyles = makeUseStyles((theme) => ({
  container: {
    background: theme.palette.background.paper,
    padding: theme.spacing(0),
    borderRadius: theme.measure.borderRadius.large,
    width: "100%",
  },
  inputWrapper: {
    position: "relative",
    width: "100%",
  },
  input: {
    width: "100%",
    "& textarea": {
      maxHeight: "250px !important",
      marginBottom: ({ tagsHeight, totalCount, selectorIsOpen }: StyleProps) => {
        const basePadding = 5
        const tagsSpace = tagsHeight ? Math.ceil(tagsHeight / 8) : 0
        const buffer =
          3 + (totalCount > 0 ? (selectorIsOpen ? 3 : 0) : selectorIsOpen ? 8 : 0)
        return theme.spacing(basePadding + tagsSpace + buffer)
      },
    },
  },
  controls: {
    position: "absolute",
    bottom: theme.spacing(1),
    left: theme.spacing(1),
    right: theme.spacing(1),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    zIndex: 1,
  },
  leftControls: {
    display: "flex",
    alignItems: "center",
  },
  controlButton: {
    background: "transparent",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  dropdown: {
    minWidth: "150px",
  },
  aiToolButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1),
  },
  aiToolLabelText: {
    marginLeft: theme.spacing(0.5),
  },
  selectorWrapper: {
    position: "absolute",
    bottom: theme.spacing(7),
    left: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1,
    background: theme.palette.groovy.neutral[100],
    borderRadius: theme.measure.borderRadius.medium,
    padding: theme.spacing(1),
  },
  selectedItemsWrapper: {
    position: "absolute",
    bottom: theme.spacing(6), // Fixed position above controls
    left: theme.spacing(1),
    right: theme.spacing(1),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    zIndex: 1,
  },
  trainingDataSelectorContainer: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    width: "100%",

    "& > div": {
      flexGrow: 1, // Make training data selector take up the rest of the space
    },
  },
  moduleContentCheckboxContainer: {
    paddingTop: theme.spacing(1.5),

    "& > span": {
      padding: theme.spacing(0, 1),
    },
  },
  aiButton: {
    width: "40px",
    height: "40px",
  },
  divider: {
    margin: theme.spacing(0, -0.65),
    width: "20px",
  },
}))

export default observer(GenerateWithAIForm)
